import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  useWindowDimensions,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import apiClient, { projectAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { StudentProjectListItem } from '../types';
import { mapProjectListItem } from '../utils/mappers';
import { Colors, Spacing, FontSize, BorderRadius } from '../theme/colors';
import { Toast } from '../components/common/Toast';
import { useTheme, ThemeColors } from '../context/ThemeContext';

function getStatusColor(status: string) {
  switch (status) {
    case 'Graded':
      return { bg: '#DCFCE7', text: '#16A34A' };
    case 'Submitted':
      return { bg: '#E0F2FE', text: '#0284C7' };
    case 'Late':
      return { bg: '#FEE2E2', text: '#DC2626' };
    default:
      return { bg: '#FEF3C7', text: '#D97706' };
  }
}

function getStatusLabel(status: string) {
  return status === 'NotSubmitted' ? 'Not Submitted' : status;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Same format/size rules as the web frontend's ParentProjectDetail.tsx, so a parent
// gets the same validation whether they submit from the web app or this web build.
const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'zip'];
const MAX_ATTACHMENT_SIZE_MB = 15;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export function ProjectDetailScreen() {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const { project: initialProject } = route.params as { project: StudentProjectListItem };
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [detail, setDetail] = useState<StudentProjectListItem>(initialProject);
  const [submissionText, setSubmissionText] = useState(initialProject.mySubmissionText ?? '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [showToast, setShowToast] = useState(false);

  const showToastMsg = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setShowToast(true);
  };

  const refreshDetail = useCallback(async () => {
    if (!selectedStudent) return;
    try {
      const { data } = await projectAPI.getProjectsByClass(selectedStudent.classId, selectedStudent.enrollmentId);
      const mapped = (data?.data ?? []).map(mapProjectListItem);
      const updated = mapped.find((p: StudentProjectListItem) => p.id === detail.id);
      if (updated) {
        setDetail(updated);
        setSubmissionText(updated.mySubmissionText ?? '');
        setSelectedFile(null);
      }
    } catch (err) {
      console.error('Failed to refresh project detail:', err);
    }
  }, [selectedStudent, detail.id]);

  const handleDownload = async (url: string, fallbackName: string) => {
    if (!url) return;
    try {
      setDownloadingUrl(url);
      const response = await apiClient.get(url);
      const data = response.data;

      if (data && data.fileBase64) {
        const contentType = data.contentType || 'application/octet-stream';
        const base64data = `data:${contentType};base64,${data.fileBase64}`;
        const fileName = data.fileName || fallbackName;

        const link = (globalThis as any).document.createElement('a');
        link.href = base64data;
        link.download = fileName;
        (globalThis as any).document.body.appendChild(link);
        link.click();
        (globalThis as any).document.body.removeChild(link);
        showToastMsg('Download started');
      } else {
        throw new Error('Invalid attachment data format');
      }
    } catch (err) {
      console.error('Failed to download file:', err);
      showToastMsg('Unable to fetch the file at this time.', 'error');
    } finally {
      setDownloadingUrl(null);
    }
  };

  const handleChooseFile = () => {
    const input = (globalThis as any).document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_ATTACHMENT_EXTENSIONS.map(ext => `.${ext}`).join(',');
    input.onchange = (e: any) => {
      const file: File | undefined = e.target.files?.[0];
      if (!file) return;

      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
        setFormError('Unsupported file format. Please upload a PDF, Word, PowerPoint, Excel, ZIP, or image (JPG, PNG, WEBP) file.');
        return;
      }
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        const sizeLabel = file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
        setFormError(`File is too large (${sizeLabel}). Maximum allowed size is ${MAX_ATTACHMENT_SIZE_MB}MB.`);
        return;
      }

      setFormError(null);
      setSelectedFile(file);
    };
    input.click();
  };

  const handleSubmit = async () => {
    if (!selectedStudent) return;
    if (!submissionText.trim() && !selectedFile) {
      showToastMsg('Add a note or attach a file before submitting.', 'error');
      return;
    }
    if (formError) {
      showToastMsg('Fix the attachment error before submitting.', 'error');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      const formData = new FormData();
      formData.append('projectId', detail.id);
      formData.append('studentId', selectedStudent.enrollmentId);
      if (submissionText) formData.append('submissionText', submissionText);
      if (selectedFile) formData.append('FileUrl', selectedFile);

      await projectAPI.submitProject(formData);
      showToastMsg('Project submitted successfully');
      await refreshDetail();
    } catch (err: any) {
      console.error('Project submission failed:', err);
      const message = err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit project. Please try again.';
      setFormError(message);
      showToastMsg(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const displayStatus = getStatusLabel(detail.myStatus);
  const statusColors = getStatusColor(detail.myStatus);
  const isOnline = detail.submissionType !== 'Physical';
  const isPastDue = new Date() > new Date(detail.dueDate);
  const lateWindowClosed = detail.allowLateSubmission && detail.lateSubmissionDueDate
    ? new Date() > new Date(detail.lateSubmissionDueDate)
    : isPastDue;
  const alreadySubmitted = detail.myStatus !== 'NotSubmitted';
  const isGraded = detail.myStatus === 'Graded';
  const canSubmit = !alreadySubmitted && (!isPastDue || (detail.allowLateSubmission && !lateWindowClosed));

  return (
    <View style={styles.modalOverlay}>
      <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => navigation.goBack()} />
      <View style={[styles.modalContent, isMobile && styles.modalContentMobile]}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={[styles.header, isMobile && styles.headerMobile]}>
            <Text style={styles.headerTitle}>Project Detail</Text>
            <TouchableOpacity style={styles.modalCloseBtn} onPress={() => navigation.goBack()}>
              <FeatherIcon name="x" size={20} color={colors.textPrimary} />
            </TouchableOpacity>
          </View>

          <View style={[styles.card, isMobile && styles.cardMobile]}>
            <View style={[styles.cardTop, isMobile && styles.cardTopMobile]}>
              <View style={styles.subjectBadge}>
                <Text style={styles.subjectText}>{detail.subjectName}</Text>
              </View>
              <View style={[styles.statusBadge, { backgroundColor: statusColors.bg }]}>
                <Text style={[styles.statusText, { color: statusColors.text }]}>{displayStatus}</Text>
              </View>
            </View>

            <Text style={styles.title}>{detail.title}</Text>

            <View style={styles.dates}>
              <View style={styles.dateRow}>
                <FeatherIcon name="clock" size={16} color={colors.textMuted} />
                <View style={styles.dateInfo}>
                  <Text style={styles.dateLabel}>Due Date</Text>
                  <Text style={styles.dateValue}>{formatDate(detail.dueDate)}</Text>
                </View>
              </View>
              {detail.mySubmittedDate && (
                <View style={styles.dateRow}>
                  <FeatherIcon name="check-circle" size={16} color="#10B981" />
                  <View style={styles.dateInfo}>
                    <Text style={styles.dateLabel}>Submitted</Text>
                    <Text style={styles.dateValue}>{formatDate(detail.mySubmittedDate)}</Text>
                  </View>
                </View>
              )}
              {typeof detail.totalMarks === 'number' && (
                <View style={styles.dateRow}>
                  <FeatherIcon name="target" size={16} color={colors.textMuted} />
                  <View style={styles.dateInfo}>
                    <Text style={styles.dateLabel}>Total Marks</Text>
                    <Text style={styles.dateValue}>{detail.totalMarks}</Text>
                  </View>
                </View>
              )}
            </View>

            <View style={styles.divider} />

            {isGraded && (
              <View style={styles.gradeCard}>
                <View style={styles.gradeRow}>
                  <FeatherIcon name="award" size={18} color="#16A34A" />
                  <Text style={styles.gradeScore}>
                    {detail.myReceivedMarks ?? 0}
                    {typeof detail.totalMarks === 'number' ? ` / ${detail.totalMarks}` : ''} pts
                  </Text>
                  {detail.myGrade && <Text style={styles.gradeLetter}>{detail.myGrade}</Text>}
                </View>
                {detail.myTeacherComments && (
                  <>
                    <Text style={styles.gradeFeedbackLabel}>Teacher feedback</Text>
                    <Text style={styles.gradeFeedbackText}>{detail.myTeacherComments}</Text>
                  </>
                )}
              </View>
            )}

            <View>
              <Text style={styles.sectionTitle}>Description</Text>
              <Text style={styles.description}>{detail.description || 'No description provided.'}</Text>
            </View>

            {detail.learningObjectives && (
              <View>
                <Text style={styles.sectionTitle}>Learning Objectives</Text>
                <Text style={styles.description}>{detail.learningObjectives}</Text>
              </View>
            )}

            {detail.materialsRequired && (
              <View>
                <Text style={styles.sectionTitle}>Materials / Requirements</Text>
                <Text style={styles.description}>{detail.materialsRequired}</Text>
              </View>
            )}

            {detail.attachmentUrl && (
              <View style={styles.attachmentsSection}>
                <Text style={styles.sectionTitle}>Attachment from Teacher</Text>
                <TouchableOpacity
                  style={styles.attachmentItem}
                  onPress={() => handleDownload(`/Project/download/attachment/${detail.id}`, 'Project_Attachment')}
                  disabled={downloadingUrl === `/Project/download/attachment/${detail.id}`}
                >
                  <View style={styles.attachmentIconBox}>
                    <FeatherIcon name="file" size={16} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.attachmentName} numberOfLines={1}>Project Attachment</Text>
                    <Text style={styles.attachmentHint}>
                      {downloadingUrl === `/Project/download/attachment/${detail.id}` ? 'Fetching...' : 'Download'}
                    </Text>
                  </View>
                  {downloadingUrl === `/Project/download/attachment/${detail.id}` ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <FeatherIcon name="download" size={18} color={colors.primary} />
                  )}
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.submissionSection}>
              <Text style={styles.sectionTitle}>Your submission</Text>

              {!isOnline ? (
                <Text style={styles.description}>
                  This project is submitted physically in class — no online submission needed.
                </Text>
              ) : alreadySubmitted ? (
                <>
                  {detail.mySubmissionText && (
                    <Text style={styles.description}>{detail.mySubmissionText}</Text>
                  )}
                  {detail.mySubmissionId && detail.myFileUrl && (
                    <TouchableOpacity
                      style={styles.attachmentItem}
                      onPress={() => handleDownload(`/ProjectSubmission/download/${detail.mySubmissionId}`, 'My_Submission')}
                      disabled={downloadingUrl === `/ProjectSubmission/download/${detail.mySubmissionId}`}
                    >
                      <View style={styles.attachmentIconBox}>
                        <FeatherIcon name="paperclip" size={16} color={colors.primary} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.attachmentName} numberOfLines={1}>Your submitted file</Text>
                        <Text style={styles.attachmentHint}>
                          {downloadingUrl === `/ProjectSubmission/download/${detail.mySubmissionId}` ? 'Fetching...' : 'Download'}
                        </Text>
                      </View>
                      {downloadingUrl === `/ProjectSubmission/download/${detail.mySubmissionId}` ? (
                        <ActivityIndicator size="small" color={colors.primary} />
                      ) : (
                        <FeatherIcon name="download" size={18} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  )}
                  {!isGraded && (
                    <Text style={[styles.description, { marginTop: Spacing.sm }]}>
                      Submitted successfully. Your teacher will grade it and publish the result here.
                    </Text>
                  )}
                </>
              ) : !canSubmit ? (
                <Text style={styles.description}>The submission window for this project has closed.</Text>
              ) : (
                <>
                  <Text style={styles.fieldLabel}>Notes</Text>
                  <TextInput
                    style={styles.textInput as any}
                    multiline
                    numberOfLines={6}
                    placeholder="Add notes about your submission..."
                    placeholderTextColor={colors.textMuted}
                    value={submissionText}
                    onChangeText={setSubmissionText}
                    editable={!submitting}
                  />

                  <View style={{ marginTop: Spacing.md }}>
                    <Text style={styles.fieldLabel}>Attach a file</Text>
                    <TouchableOpacity style={styles.chooseFileBtn} onPress={handleChooseFile} disabled={submitting}>
                      <FeatherIcon name="upload" size={16} color={colors.primary} style={{ marginRight: 8 }} />
                      <Text style={styles.chooseFileText}>
                        {selectedFile ? selectedFile.name : `Choose a file — PDF, Word, PowerPoint, Excel, ZIP, JPG, PNG, WEBP (max ${MAX_ATTACHMENT_SIZE_MB}MB)`}
                      </Text>
                    </TouchableOpacity>

                    {selectedFile && (
                      <View style={styles.selectedFileRow}>
                        <FeatherIcon name="paperclip" size={14} color={colors.textSecondary} />
                        <Text style={styles.selectedFileName} numberOfLines={1}>{selectedFile.name}</Text>
                        <TouchableOpacity onPress={() => setSelectedFile(null)} disabled={submitting}>
                          <FeatherIcon name="x" size={16} color={colors.textMuted} />
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>

                  {formError && <Text style={styles.errorText}>{formError}</Text>}

                  <TouchableOpacity
                    style={[styles.submitButton, (submitting || !!formError) && styles.submitButtonDisabled]}
                    onPress={handleSubmit}
                    disabled={submitting || !!formError}
                  >
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" size="small" />
                    ) : (
                      <Text style={styles.submitButtonText}>Submit Project</Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </ScrollView>

        <Toast
          visible={showToast}
          message={toastMessage}
          type={toastType}
          onDismiss={() => setShowToast(false)}
        />
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colors.card,
    borderRadius: 20,
    width: '90%',
    maxWidth: 600,
    maxHeight: '90%',
  } as any,
  modalContentMobile: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    borderRadius: 0,
  } as any,
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    paddingTop: 24,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerMobile: {
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  headerTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary, flex: 1 },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: colors.card,
    borderRadius: BorderRadius.lg,
    padding: Spacing.lg,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 15,
    elevation: 2,
  },
  cardMobile: {
    padding: Spacing.md,
  },
  cardTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: Spacing.md },
  cardTopMobile: { flexWrap: 'wrap', gap: 8 } as any,
  subjectBadge: {
    backgroundColor: colors.primary + '15',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: BorderRadius.full,
  },
  subjectText: { color: colors.primary, fontSize: FontSize.sm, fontWeight: '600' },
  statusBadge: { paddingHorizontal: Spacing.md, paddingVertical: Spacing.xs, borderRadius: BorderRadius.full },
  statusText: { fontSize: FontSize.sm, fontWeight: '600' },
  title: { fontSize: FontSize.xl, fontWeight: '700', color: colors.textPrimary, marginBottom: Spacing.xs },
  dates: { gap: Spacing.sm, marginBottom: Spacing.md } as any,
  dateRow: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm } as any,
  dateInfo: { flex: 1, marginLeft: Spacing.sm },
  dateLabel: { fontSize: FontSize.xs, color: colors.textMuted, fontWeight: '600', marginBottom: 2 },
  dateValue: { fontSize: FontSize.sm, color: colors.textPrimary, fontWeight: '500' },
  divider: { height: 1, backgroundColor: colors.border, marginVertical: Spacing.md },
  gradeCard: {
    backgroundColor: '#F0FDF4',
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    borderColor: '#BBF7D0',
    padding: Spacing.md,
    marginBottom: Spacing.md,
  },
  gradeRow: { flexDirection: 'row', alignItems: 'center', gap: 8 } as any,
  gradeScore: { fontSize: FontSize.lg, fontWeight: '800', color: '#16A34A' },
  gradeLetter: {
    marginLeft: 'auto',
    backgroundColor: '#16A34A',
    color: '#FFFFFF',
    fontSize: FontSize.xs,
    fontWeight: '800',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: BorderRadius.full,
  } as any,
  gradeFeedbackLabel: {
    fontSize: FontSize.xs,
    color: '#166534',
    fontWeight: '700',
    marginTop: Spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  gradeFeedbackText: { fontSize: FontSize.sm, color: '#166534', marginTop: 4, lineHeight: 20 },
  sectionTitle: { fontSize: FontSize.lg, fontWeight: '600', color: colors.textPrimary, marginBottom: Spacing.sm, marginTop: Spacing.md },
  description: { fontSize: FontSize.md, color: colors.textPrimary, lineHeight: 24 },
  attachmentsSection: { marginTop: Spacing.lg },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    padding: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: 12,
    gap: 12,
  } as any,
  attachmentIconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.card,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  attachmentName: { fontSize: 15, fontWeight: '700', color: colors.textPrimary, marginBottom: 4 },
  attachmentHint: { fontSize: 12, color: colors.textSecondary, fontWeight: '500' },
  submissionSection: { marginTop: Spacing.lg },
  fieldLabel: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textSecondary, marginBottom: Spacing.xs },
  textInput: {
    backgroundColor: colors.background,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: Spacing.md,
    fontSize: FontSize.md,
    color: colors.textPrimary,
    minHeight: 120,
    textAlignVertical: 'top',
    outlineStyle: 'none',
  } as any,
  chooseFileBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.md,
    paddingVertical: 12,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
    alignSelf: 'flex-start',
  },
  chooseFileText: { fontSize: FontSize.md, fontWeight: '600', color: colors.textPrimary },
  selectedFileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.divider,
    borderRadius: 8,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    marginTop: Spacing.sm,
  } as any,
  selectedFileName: { flex: 1, fontSize: FontSize.sm, color: colors.textPrimary, fontWeight: '600' },
  errorText: { marginTop: Spacing.sm, fontSize: FontSize.sm, color: Colors.error, fontWeight: '600' },
  submitButton: {
    backgroundColor: colors.primary,
    borderRadius: BorderRadius.md,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: Spacing.md,
  },
  submitButtonDisabled: { opacity: 0.6 },
  submitButtonText: { color: '#FFFFFF', fontSize: FontSize.md, fontWeight: '700' },
});
